import { type Camera, Group, Quaternion, Vector2, Vector3 } from "three";

import type { FigureData } from "../data/octagonOnCube";
import { Dots } from "./Dots";
import { Figure } from "./Figure";

/** メタボール点を順に受け取るシンク (画面座標 px,py と重み weight)。 */
export interface MetaballPointSink {
  setPoint(i: number, x: number, y: number, weight: number): void;
  readonly maxPoints: number;
}

const SQRT3_2 = Math.sqrt(3) / 2;

interface Copy {
  group: Group;
  figure: Figure;
  dots: Dots;
  q: number;
  r: number;
  dist: number; // 中心からの六角格子距離 (1, 2, 3...)。opacity 減衰に使う。
}

/**
 * 中心の cube の周囲を「カメラ視軸に垂直な平面上の六角格子」で埋める万華鏡的
 * パターン。各セルにメインと同じ大きさ・同じ表示 (lines / dots / both) の cube
 * コピーを置き、メインと同じ回転で完全同期させる。
 *
 * 仕様:
 *   - 六角格子 (axial 座標 q, r) で rings 段ぶんのセルを生成し、中心セル (0,0)
 *     を除いた全セルにコピーを配置 → 空白をなるべく詰める
 *   - 各コピーはメイン cube の root.quaternion をコピーして回転同期
 *   - line / dot の色・太さ・サイズ・opacity・occlusion はメインと一致させる
 *     ため set 系で外部から流し込む
 *   - per-vertex のオクルージョン可視性は全コピーで共通 (同じ回転) なので、
 *     メインで計算済みの finalOpacities をそのまま applyVertexOpacities で流用
 *
 * パフォーマンス上の注意:
 *   - コピー数 = 3 * rings * (rings + 1) (rings=1→6, 2→18, 3→36)
 *   - ドットは CSS2DObject (DOM) なのでコピー数 × 24 個の DOM が増える
 *     (rings=2 で 432)。rings は 1〜3 に制限。
 */
export class KaleidoscopeRing {
  readonly group: Group;
  private data: FigureData;
  private vertexCount: number;

  private copies: Copy[] = [];
  private rings = 0;
  // spacing は「外周頂点がちょうど重なる距離 (= 2 × 投影外接半径)」に対する倍率。
  // 1.0 = ちょうど重なる / <1 = 重なり / >1 = 隙間。実距離は update() で
  // 現在の投影外接半径から動的に算出する。
  private spacingFactor = 1.0;
  private scale = 1.0;
  // 中心からの距離 d ごとの opacity 減衰係数。各コピーの実 opacity は
  // base * copyOpacity^d。中心 (メイン cube) は本体なので影響を受けず、外側
  // ほど薄くなって「中心が主役、周囲は背景パターン」に見える。1.0 で減衰なし。
  private copyOpacity = 0.45;

  // メイン Figure / Dots と同期させる現在状態
  private lineColor = "#1a1a1a";
  private lineWidth = 1.5;
  private linesOpacity = 0.95;
  private linesVisible = true;
  private occlusionEnabled = true;
  private resolutionW = 1;
  private resolutionH = 1;
  private dotColor = "#a67c52";
  private dotSize = 100;
  private dotsVisible = true;

  // 毎フレームの再配置に使う再利用バッファ
  private readonly _viewAxis = new Vector3();
  private readonly _right = new Vector3();
  private readonly _up = new Vector3();
  private readonly _tmpVec = new Vector3();
  // 格子を cube の投影頂点の向きに合わせた基底
  private readonly _latRight = new Vector3();
  private readonly _latUp = new Vector3();
  private readonly _refUp = new Vector3(0, 1, 0);
  private readonly _refRight = new Vector3(1, 0, 0);
  // 格子角度。cube の view 軸まわりの twist (swing-twist 分解) から算出し、
  // 初回フレームで「twist と外周頂点角の差 (offset)」を較正する。以降は
  // latAngle = twist + offset で自転に滑らかに追従。連続性のため unwrap する。
  private latAngle = 0;
  private angleOffset = 0;
  private latAngleInit = false;
  // 体対角線ビューでの投影外接半径 (= 外周 12 角形の頂点までの距離)。spacing は
  // これを基準に固定算出する (毎フレーム再計算しないので脈動しない)。
  private baseRadius: number;

  /** 体対角線 (1,1,1) に直交する平面への投影距離の最大値 (外周頂点の半径)。 */
  private static computeBaseRadius(data: FigureData): number {
    const inv = 1 / Math.sqrt(3);
    let maxPerp = 0;
    for (const [x, y, z] of data.vertices) {
      const along = (x + y + z) * inv;
      const perp = Math.sqrt(Math.max(0, x * x + y * y + z * z - along * along));
      if (perp > maxPerp) maxPerp = perp;
    }
    return maxPerp;
  }

  constructor(data: FigureData) {
    this.data = data;
    this.vertexCount = data.vertices.length;
    this.group = new Group();
    this.group.visible = false;
    this.baseRadius = KaleidoscopeRing.computeBaseRadius(data);
  }

  /**
   * 八角形比率の切替などで頂点データが変わったとき、新データに差し替える。
   * baseRadius を再計算し、既存コピーを新データで作り直す。
   */
  setData(data: FigureData): void {
    this.data = data;
    this.vertexCount = data.vertices.length;
    this.baseRadius = KaleidoscopeRing.computeBaseRadius(data);
    this.latAngleInit = false; // 角度較正もやり直す
    const rings = this.rings;
    this.rings = -1; // 強制再構築のため一旦無効値に
    this.rebuild(rings);
  }

  /** 六角格子の段数 (1〜3) を変更。コピーを作り直す。 */
  setRings(n: number): void {
    n = Math.max(0, Math.min(3, Math.floor(n)));
    if (n === this.rings) return;
    this.rebuild(n);
  }

  private rebuild(rings: number): void {
    // 既存コピー破棄
    for (const c of this.copies) {
      this.group.remove(c.group);
      c.figure.dispose();
    }
    this.copies = [];
    this.rings = rings;
    // 角度追跡をリセット (次の update で最外頂点の生角度から再初期化)。
    this.latAngleInit = false;

    // 六角格子のセル (中心 (0,0) を除く) を axial 座標で生成。
    // cube 座標 (x=q, z=r, y=-q-r) の距離 = max(|q|,|r|,|q+r|) <= rings。
    for (let q = -rings; q <= rings; q++) {
      for (let r = -rings; r <= rings; r++) {
        const dist = Math.max(Math.abs(q), Math.abs(r), Math.abs(q + r));
        if (dist > rings) continue;
        if (q === 0 && r === 0) continue; // 中心はメイン cube
        const group = new Group();
        const figure = new Figure(this.data);
        const dots = new Dots(this.data);
        const mul = Math.pow(this.copyOpacity, dist);
        figure.setLineColor(this.lineColor);
        figure.setLineWidth(this.lineWidth);
        figure.setLinesOpacity(this.linesOpacity * mul);
        figure.setShowLines(this.linesVisible);
        figure.setOcclusion(this.occlusionEnabled);
        figure.setResolution(this.resolutionW, this.resolutionH);
        dots.setColor(this.dotColor);
        dots.setSize(this.dotSize);
        dots.setVisible(this.dotsVisible);
        group.add(figure.group);
        group.add(dots.group);
        this.group.add(group);
        this.copies.push({ group, figure, dots, q, r, dist });
      }
    }
  }

  /** spacing 倍率 (1.0 = 外周頂点がちょうど重なる)。 */
  setSpacingFactor(f: number): void {
    this.spacingFactor = f;
  }

  setScale(s: number): void {
    this.scale = s;
  }

  setVisible(v: boolean): void {
    this.group.visible = v;
  }

  setLineColor(color: string): void {
    this.lineColor = color;
    for (const c of this.copies) c.figure.setLineColor(color);
  }

  setLineWidth(w: number): void {
    this.lineWidth = w;
    for (const c of this.copies) c.figure.setLineWidth(w);
  }

  setLinesOpacity(o: number): void {
    this.linesOpacity = o;
    for (const c of this.copies) {
      c.figure.setLinesOpacity(o * Math.pow(this.copyOpacity, c.dist));
    }
  }

  /** 距離ごとの opacity 減衰係数を設定 (1.0 で減衰なし、小さいほど外周が薄い)。 */
  setCopyOpacity(v: number): void {
    this.copyOpacity = v;
    // 現在の lines opacity を新しい減衰で再適用 (dots は次フレームの
    // applyVertexOpacities で反映される)。
    this.setLinesOpacity(this.linesOpacity);
  }

  setShowLines(v: boolean): void {
    this.linesVisible = v;
    for (const c of this.copies) c.figure.setShowLines(v);
  }

  setOcclusion(enabled: boolean): void {
    this.occlusionEnabled = enabled;
    for (const c of this.copies) c.figure.setOcclusion(enabled);
  }

  setResolution(w: number, h: number): void {
    this.resolutionW = w;
    this.resolutionH = h;
    for (const c of this.copies) c.figure.setResolution(w, h);
  }

  setDotColor(color: string): void {
    this.dotColor = color;
    for (const c of this.copies) c.dots.setColor(color);
  }

  setDotSize(sizePx: number): void {
    this.dotSize = sizePx;
    for (const c of this.copies) c.dots.setSize(sizePx);
  }

  setDotsVisible(v: boolean): void {
    this.dotsVisible = v;
    for (const c of this.copies) c.dots.setVisible(v);
  }

  /**
   * メインで計算済みの per-vertex 最終 opacity (occlusion × mode) を全コピーの
   * ドットに流用する。全コピーはメインと同じ回転なので可視性は完全に一致する。
   */
  applyVertexOpacities(finalOpacities: number[]): void {
    if (!this.group.visible) return;
    for (const c of this.copies) {
      const mul = Math.pow(this.copyOpacity, c.dist);
      const children = c.dots.group.children;
      for (let i = 0; i < this.vertexCount; i++) {
        const op = (finalOpacities[i] ?? 1) * mul;
        const renderOn = op > 0.001;
        if (children[i]) children[i].visible = renderOn;
        c.dots.setOpacity(i, op);
      }
    }
  }

  /**
   * 毎フレーム: コピーを view 軸に垂直な平面上の六角格子に配置し、回転を
   * メイン quaternion に揃える。
   */
  update(cameraPos: Vector3, mainQuat: Quaternion): void {
    if (!this.group.visible || this.copies.length === 0) return;

    // view 軸: 原点 → カメラ位置
    this._viewAxis.copy(cameraPos).normalize();

    // view 軸と平行な参照ベクトルだと cross が縮退する。Y 軸寄りなら X、
    // それ以外なら Y を参照に取って直交基底 (カメラ固定の right/up) を作る。
    const ref = Math.abs(this._viewAxis.y) > 0.95 ? this._refRight : this._refUp;
    this._right.crossVectors(this._viewAxis, ref).normalize();
    this._up.crossVectors(this._viewAxis, this._right).normalize();

    // cube の view 軸まわりの twist (in-plane 自転角) を swing-twist 分解で
    // 取得する。これは quaternion から連続的に求まるので、最外頂点を毎フレーム
    // 選び直す方式のようなチラつき・30° 飛びが起きない。
    //   pure twist は q = (axis*sin(θ/2), cos(θ/2)) なので
    //   θ = 2*atan2(q.xyz·axis, q.w)
    const proj =
      mainQuat.x * this._viewAxis.x +
      mainQuat.y * this._viewAxis.y +
      mainQuat.z * this._viewAxis.z;
    const twist = 2 * Math.atan2(proj, mainQuat.w);

    // 初回 (または rebuild 後) は、現在の cube 姿勢での外周頂点角を実測し、
    // 「twist と外周頂点角の差 (offset)」を較正する。以降は twist + offset で
    // 外周頂点方向を再現する (純自転中はぴったり追従)。
    if (!this.latAngleInit) {
      let maxR2 = 0;
      let bestPx = 1;
      let bestPy = 0;
      const verts = this.data.vertices;
      for (let i = 0; i < verts.length; i++) {
        this._tmpVec
          .set(verts[i][0], verts[i][1], verts[i][2])
          .applyQuaternion(mainQuat);
        const px = this._tmpVec.dot(this._right);
        const py = this._tmpVec.dot(this._up);
        const r2 = px * px + py * py;
        if (r2 > maxR2) {
          maxR2 = r2;
          bestPx = px;
          bestPy = py;
        }
      }
      const a0 = Math.atan2(bestPy, bestPx);
      this.angleOffset = a0 - twist;
      this.latAngle = a0;
      this.latAngleInit = true;
    } else {
      // twist + offset を前フレームから連続になるよう unwrap (±π に収める)。
      const target = twist + this.angleOffset;
      let delta = target - this.latAngle;
      delta -= 2 * Math.PI * Math.round(delta / (2 * Math.PI));
      this.latAngle += delta;
    }
    const ax = Math.cos(this.latAngle);
    const ay = Math.sin(this.latAngle);

    // 格子基底: latRight = 自転に追従した角度方向、latUp = それに直交 (view 平面内)。
    this._latRight
      .copy(this._right)
      .multiplyScalar(ax)
      .addScaledVector(this._up, ay);
    this._latUp
      .copy(this._right)
      .multiplyScalar(-ay)
      .addScaledVector(this._up, ax);

    // 外周頂点がちょうど重なる中心間距離 = 2 × baseRadius × scale (固定)。
    // 体対角線ビューの外接半径を基準にしているので毎フレーム脈動しない。
    const worldSpacing = this.spacingFactor * 2 * this.baseRadius * this.scale;

    for (const c of this.copies) {
      // 六角格子 axial (q, r) → 平面座標。lattice vector a=latRight, b=右上 60°。
      const px = worldSpacing * (c.q + 0.5 * c.r);
      const py = worldSpacing * (SQRT3_2 * c.r);
      c.group.position
        .copy(this._latRight)
        .multiplyScalar(px)
        .addScaledVector(this._latUp, py);
      c.group.quaternion.copy(mainQuat);
      c.group.scale.setScalar(this.scale);
    }
  }

  /** 全コピーの各頂点ドットに per-vertex サイズを適用 (heartbeat パルス用)。 */
  applyVertexSizes(sizes: ArrayLike<number>): void {
    for (const c of this.copies) {
      for (let i = 0; i < this.vertexCount; i++) {
        c.dots.setSizeAt(i, sizes[i] ?? 0);
      }
    }
  }

  /**
   * 各コピーの可視頂点を画面へ投影し、メタボール点として sink に追加する。
   * startIndex から詰め、追加後の次インデックスを返す。weight は
   * baseWeights[i] (中心 cube の occlusion フェード) × そのコピーの falloff。
   * sink.maxPoints を超えないよう打ち切る。
   *
   * 注意: copies の group.matrixWorld が最新である前提 (renderer.render 後 or
   * scene.updateMatrixWorld 後に呼ぶこと)。
   */
  appendMetaballPoints(
    sink: MetaballPointSink,
    startIndex: number,
    camera: Camera,
    db: Vector2,
    baseWeights: ArrayLike<number>,
  ): number {
    let n = startIndex;
    const verts = this.data.vertices;
    for (const c of this.copies) {
      const mul = Math.pow(this.copyOpacity, c.dist);
      for (let i = 0; i < verts.length; i++) {
        const w = (baseWeights[i] ?? 1) * mul;
        if (w < 0.01) continue; // 非表示頂点はスキップ
        if (n >= sink.maxPoints) return n; // 上限で打ち切り
        this._tmpVec
          .set(verts[i][0], verts[i][1], verts[i][2])
          .applyMatrix4(c.group.matrixWorld)
          .project(camera);
        const px = (this._tmpVec.x * 0.5 + 0.5) * db.x;
        const py = (this._tmpVec.y * 0.5 + 0.5) * db.y;
        sink.setPoint(n, px, py, w);
        n++;
      }
    }
    return n;
  }

  /** 全コピーを破棄してリソース解放。unmount 時に呼ぶ。 */
  dispose(): void {
    for (const c of this.copies) {
      this.group.remove(c.group);
      c.figure.dispose();
    }
    this.copies = [];
    this.rings = 0;
  }
}
